import { useCallback, useMemo } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useTheme } from '@mui/material/styles';

const DEFAULT_LOCALE_PREFIX = 'CATALOG_PICKER_TOUR';

const LOCALE_DEFAULTS = {
  BACK: 'Back',
  CLOSE: 'Close',
  LAST: 'Done',
  NEXT: 'Next',
  SKIP: 'Skip',
};

/**
 * Shared themed react-joyride wrapper for catalog picker dialogs.
 * Consumers pass steps; styling and finish lifecycle stay here.
 */
function ThemedJoyrideTour({
  run,
  onRunChange,
  steps,
  formatMessage,
  locale: localeOverride,
  localePrefix = DEFAULT_LOCALE_PREFIX,
  onComplete,
  ...joyrideOverrides
}) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const locale = useMemo(() => {
    if (localeOverride) {
      return localeOverride;
    }
    if (!formatMessage) {
      return {
        back: LOCALE_DEFAULTS.BACK,
        close: LOCALE_DEFAULTS.CLOSE,
        last: LOCALE_DEFAULTS.LAST,
        next: LOCALE_DEFAULTS.NEXT,
        skip: LOCALE_DEFAULTS.SKIP,
      };
    }
    return {
      back: formatMessage({
        id: `${localePrefix}_BACK`,
        defaultMessage: LOCALE_DEFAULTS.BACK,
      }),
      close: formatMessage({
        id: `${localePrefix}_CLOSE`,
        defaultMessage: LOCALE_DEFAULTS.CLOSE,
      }),
      last: formatMessage({
        id: `${localePrefix}_LAST`,
        defaultMessage: LOCALE_DEFAULTS.LAST,
      }),
      next: formatMessage({
        id: `${localePrefix}_NEXT`,
        defaultMessage: LOCALE_DEFAULTS.NEXT,
      }),
      skip: formatMessage({
        id: `${localePrefix}_SKIP`,
        defaultMessage: LOCALE_DEFAULTS.SKIP,
      }),
    };
  }, [formatMessage, localeOverride, localePrefix]);

  const handleCallback = useCallback(
    data => {
      const { status, type, action } = data;
      const closed =
        action === ACTIONS.CLOSE ||
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        (type === EVENTS.TOUR_END &&
          (action === ACTIONS.SKIP || action === ACTIONS.CLOSE));
      if (closed) {
        if (typeof onComplete === 'function') {
          onComplete();
        }
        onRunChange(false);
      }
    },
    [onComplete, onRunChange],
  );

  const styles = useMemo(
    () => ({
      options: {
        zIndex: 10000,
        primaryColor,
        arrowColor: theme.palette.background.paper,
        backgroundColor: theme.palette.background.paper,
        textColor: theme.palette.text.primary,
        overlayColor: 'rgba(0, 0, 0, 0.5)',
      },
      buttonNext: {
        backgroundColor: primaryColor,
        color: theme.palette.primary.contrastText || '#fff',
        borderRadius: 4,
        fontSize: 14,
      },
      buttonBack: {
        color: primaryColor,
        marginRight: 8,
      },
      buttonSkip: {
        color: theme.palette.text.secondary,
      },
      buttonClose: {
        color: theme.palette.text.secondary,
      },
      spotlight: {
        borderRadius: 8,
      },
      tooltip: {
        borderRadius: 8,
        padding: 16,
      },
      tooltipTitle: {
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 8,
      },
      tooltipContent: {
        fontSize: 14,
        padding: '0 0 8px',
      },
    }),
    [primaryColor, theme],
  );

  return (
    <Joyride
      run={run}
      steps={steps}
      continuous
      showProgress
      showSkipButton
      disableOverlayClose
      spotlightClicks={false}
      scrollToFirstStep
      callback={handleCallback}
      locale={locale}
      styles={styles}
      {...joyrideOverrides}
    />
  );
}

export default ThemedJoyrideTour;
